import {
  type ExtendedPaymentInfoFormData,
  type SelectInputItem,
  type CreditCard,
  type PaymentInfoProps,
  type CardErrors,
  PaymentMethod,
} from '@openeducation/pp-rn-shared-types';
import { styled } from '@openeducation/pp-rn-themes';
import { useTranslation } from 'react-i18next';

import {
  PaymentInfoCardType,
  PaymentInfoCardNumber,
  PaymentInfoHolderName,
  PaymentInfoCvv,
  PaymentInfoInstallments,
  PaymentInfoExpirationDate,
  PaymentInfoEmail,
  PaymentInfoAmount,
} from './fields';
import { CardWrapper, CardPreviewContainer } from './payment-info';
import { PPText } from '../../atoms/pp-text/pp-text';
import { AdditionalPaymentInfo } from '../additional-payment-info/additional-payment-info';

type UseCardRenderingProps = Omit<
  PaymentInfoProps,
  'validation' | 'paymentMethodOnFile'
> & {
  isTwoCreditCards: boolean;
  cardErrors: CardErrors;
  handleFocus: (fieldName: string) => void;
  handleBlur: () => void;
  handleCard1AmountChange: (value: string) => void;
  handleCard1AmountBlur: (value: string) => void;
  card1AmountError?: string;
  focusedCardFieldType?: string;
};

export const usePaymentMethodRenderer = (props: UseCardRenderingProps) => {
  const { t } = useTranslation();

  const renderPaymentMethodFields = (cardIndex: 1 | 2) => {
    const {
      formData,
      onFieldChange,
      selectData,
      fields: {
        isCardTypeEnabled,
        isCardNumberEnabled,
        isExpirationDateEnabled,
        isHolderNameEnabled,
        isCvvEnabled,
        isInstallmentsEnabled,
        isExistingPaymentMethod,
        isPayPalEmailEnabled,
        isDraftCreditCardNumber,
      },
      renderCardPreview,
      isTwoCreditCards,
      focusedCardFieldType,
      paypalEmail,
      additionalPaymentInfoCard1,
      additionalPaymentInfoCard2,
      currency,
      mainPaymentMinPercentage,
      cardErrors,
      handleFocus,
      handleBlur,
      handleCard1AmountChange,
      handleCard1AmountBlur,
      card1AmountError,
    } = props;

    const fieldNamePrefix = `card${cardIndex}` as 'card1' | 'card2';
    const amountValue =
      cardIndex === 1 ? formData.card1_amount : formData.card2_amount;
    const handleAmountSpecificChange =
      cardIndex === 1 ? handleCard1AmountChange : undefined;
    const isAmountDisabled = cardIndex === 2;
    const amountHelperText =
      cardIndex === 1 && isTwoCreditCards
        ? t('input.card_amount.help_text.at_least_n_percent', {
            minPercentage: mainPaymentMinPercentage || '',
          })
        : cardIndex === 2 && isTwoCreditCards
        ? t('input.card_amount.help_text.remaining_payment')
        : '';

    if (
      !formData ||
      (cardIndex === 2 &&
        formData.card2_cardType === null &&
        formData.card2_cardNumber === null &&
        formData.card2_holderName === null &&
        formData.card2_expirationMonth === null &&
        formData.card2_expirationYear === null &&
        formData.card2_cvv === null &&
        formData.card2_installments === null)
    ) {
      return null;
    }

    const cardType = formData[
      `${fieldNamePrefix}_cardType` as keyof ExtendedPaymentInfoFormData
    ] as CreditCard;
    const cardNumber = formData[
      `${fieldNamePrefix}_cardNumber` as keyof ExtendedPaymentInfoFormData
    ] as string;
    const holderName = formData[
      `${fieldNamePrefix}_holderName` as keyof ExtendedPaymentInfoFormData
    ] as string;
    const cvv = formData[
      `${fieldNamePrefix}_cvv` as keyof ExtendedPaymentInfoFormData
    ] as string;
    const expirationMonth = formData[
      `${fieldNamePrefix}_expirationMonth` as keyof ExtendedPaymentInfoFormData
    ] as string;
    const expirationYear = formData[
      `${fieldNamePrefix}_expirationYear` as keyof ExtendedPaymentInfoFormData
    ] as string;
    const installmentsValue = formData[
      `${fieldNamePrefix}_installments` as keyof ExtendedPaymentInfoFormData
    ] as string;

    const currentCardErrors = cardErrors[fieldNamePrefix];
    const typeError = currentCardErrors.type;
    const numberError = currentCardErrors.number;
    const expirationError = currentCardErrors.expiration;
    const nameError = currentCardErrors.name;
    const cvvError = currentCardErrors.cvv;
    const currentCardExpiryDate = currentCardErrors.expiryDate;
    const installmentsData =
      cardIndex === 1 ? selectData.installments1 : selectData.installments2;

    const handleExpirationChange = (value: string, name?: string) => {
      if (name) {
        onFieldChange(value, `${fieldNamePrefix}_${name}`);
      }
    };

    const handleExpirationFocus = (name?: string) => {
      if (name) {
        handleFocus(`${fieldNamePrefix}_${name}`);
      }
    };

    return (
      <>
        {renderCardPreview && !isExistingPaymentMethod && (formData.paymentMethod === PaymentMethod.CREDIT_CARD || formData.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS) && (
          <CardWrapper isTwoCreditCards={isTwoCreditCards}>
            <CardPreviewContainer isTwoCreditCards={isTwoCreditCards}>
              {renderCardPreview(
                cardNumber,
                currentCardExpiryDate,
                holderName,
                cvv,
                focusedCardFieldType
              )}
            </CardPreviewContainer>
          </CardWrapper>
        )}

        {isTwoCreditCards && (
          <PaymentInfoAmount
            value={amountValue}
            error={cardIndex === 1 ? card1AmountError : undefined}
            helperText={amountHelperText}
            onValueChange={(val: string) =>
              handleAmountSpecificChange && handleAmountSpecificChange(val)
            }
            disabled={isAmountDisabled}
            onFocus={() => handleFocus(`${fieldNamePrefix}_amount`)}
            onBlur={() => {
              handleBlur();
              if (cardIndex === 1 && amountValue) {
                handleCard1AmountBlur(amountValue);
              }
            }}
            currency={currency}
          />
        )}
        {isCardTypeEnabled &&
          (cardIndex === 1 ||
            (cardIndex === 2 && formData.card2_cardType !== null)) && (
            <PaymentInfoCardType
              error={typeError}
              selectedValue={cardType}
              data={selectData.cardType as SelectInputItem[]}
              disabled={isExistingPaymentMethod && cardIndex === 1}
              onValueChange={(val: string) =>
                onFieldChange(val, `${fieldNamePrefix}_cardType`)
              }
              onFocus={() => handleFocus(`${fieldNamePrefix}_cardType`)}
              onBlur={handleBlur}
            />
          )}
        {isCardNumberEnabled &&
          (cardIndex === 1 ||
            (cardIndex === 2 && formData.card2_cardNumber !== null)) && (
            <PaymentInfoCardNumber
              error={numberError}
              value={cardNumber}
              disabled={
                (isExistingPaymentMethod && cardIndex === 1) ||
                (isDraftCreditCardNumber && cardIndex === 1)
              }
              onValueChange={(val: string) =>
                onFieldChange(val, `${fieldNamePrefix}_cardNumber`)
              }
              onFocus={() => handleFocus(`${fieldNamePrefix}_cardNumber`)}
              onBlur={handleBlur}
            />
          )}
        {isHolderNameEnabled &&
          (cardIndex === 1 ||
            (cardIndex === 2 && formData.card2_holderName !== null)) && (
            <PaymentInfoHolderName
              error={nameError}
              value={holderName}
              disabled={isExistingPaymentMethod && cardIndex === 1}
              onValueChange={(val: string) =>
                onFieldChange(val, `${fieldNamePrefix}_holderName`)
              }
              onFocus={() => handleFocus(`${fieldNamePrefix}_holderName`)}
              onBlur={handleBlur}
            />
          )}
        {isExpirationDateEnabled &&
          (cardIndex === 1 ||
            (cardIndex === 2 &&
              formData.card2_expirationMonth !== null &&
              formData.card2_expirationYear !== null)) && (
            <PaymentInfoExpirationDate
              error={expirationError}
              expirationMonthValue={expirationMonth}
              expirationMonthData={
                selectData.expirationMonth as SelectInputItem[]
              }
              expirationYearValue={expirationYear}
              expirationYearData={
                selectData.expirationYear as SelectInputItem[]
              }
              onValueChange={handleExpirationChange}
              onFocus={handleExpirationFocus}
              onBlur={handleBlur}
            />
          )}
        {(!isExistingPaymentMethod ||
          (cardIndex === 2 && formData.card2_cvv !== null)) &&
          isCvvEnabled && (
            <PaymentInfoCvv
              value={cvv}
              cardType={cardType}
              error={cvvError}
              onValueChange={(val: string) =>
                onFieldChange(val, `${fieldNamePrefix}_cvv`)
              }
              onFocus={() => handleFocus(`${fieldNamePrefix}_cvv`)}
              onBlur={handleBlur}
            />
          )}
        {isInstallmentsEnabled &&
          (cardIndex === 1 ||
            (cardIndex === 2 && formData.card2_installments !== null)) && (
            <PaymentInfoInstallments
              selectedValue={installmentsValue}
              data={installmentsData || []}
              onValueChange={(val: string) =>
                onFieldChange(val, `${fieldNamePrefix}_installments`)
              }
              onFocus={() => handleFocus(`${fieldNamePrefix}_installments`)}
              onBlur={handleBlur}
            />
          )}
        {cardIndex === 1 && isPayPalEmailEnabled && !!paypalEmail && (
          <PaymentInfoEmail value={paypalEmail} />
        )}
        {cardIndex === 1 && additionalPaymentInfoCard1 && (
          <AdditionalPaymentInfo {...additionalPaymentInfoCard1} />
        )}
        {cardIndex === 2 && additionalPaymentInfoCard2 && (
          <AdditionalPaymentInfo {...additionalPaymentInfoCard2} />
        )}
      </>
    );
  };

  const renderCardWithTitle = (cardIndex: 1 | 2) => {
    if (props.isTwoCreditCards) {
      return (
        <>
          <CardTitle>
            {t('payment.types.credit_card')} {cardIndex}
          </CardTitle>
          {renderPaymentMethodFields(cardIndex)}
        </>
      );
    }
    return renderPaymentMethodFields(cardIndex);
  };

  return { renderPaymentMethodFields, renderCardWithTitle };
};

const CardTitle = styled(PPText).attrs({
  fontType: 'semiBold',
  fontSize: 20,
})`
  color: ${({ theme }) => theme.colors.dark1};
  padding-top: 24px;
  padding-bottom: 16px;
`;
